import { useMemo } from "react";

import {
    MIN_SOL_BALANCE,
    NullableRecord,
    WRAPPED_SOL_MINT,
    bsMath,
    combineCollections,
    filterNullableRecord,
    lamportsToUiAmount
} from "@bridgesplit/utils";
import { useUserTokens } from "@bridgesplit/react";

import { useTokenMetadata } from "../reducers";
import { PriceFetchType, TokenBalanceExpanded } from "../types";
import { useActiveWallet } from "./wallet";
import { useWhirlpoolPositions, whirlpoolPositionToToken } from "./whirlpool";
import { useOraclePrices } from "./pricing";
import { raydiumPositionToToken, useRaydiumPositions } from "./raydium";
import { isRaydiumPosition, isWhirlpoolPosition } from "./clmm";

type AssetParams = {
    skip?: boolean;
    fullSolBalance?: boolean;
    includeLpPositions?: boolean;
    solBuffer?: number; //Used as a buffer for solana transactions that to make sure user has enough balance for rent and transaction fees
};

// Get the available assets for user to deposit
export function useUserAvailableAssets({ skip, fullSolBalance, includeLpPositions }: AssetParams) {
    const { activeWallet, skip: isWalletSkip } = useActiveWallet();

    const walletAssets = useWalletAvailableAssets(activeWallet, {
        skip: !activeWallet || isWalletSkip || skip,
        fullSolBalance,
        includeLpPositions
    });

    const isNotAuthenticated = useMemo(() => !activeWallet || isWalletSkip, [activeWallet, isWalletSkip]);

    const data = useMemo(() => {
        if (isNotAuthenticated) return [];
        return walletAssets;
    }, [isNotAuthenticated, walletAssets]);

    const isLoading = useMemo(() => {
        if (isNotAuthenticated) return false;
        return walletAssets === undefined;
    }, [isNotAuthenticated, walletAssets]);

    return { data, isLoading };
}

export function useWalletAvailableAssets(wallet: string | undefined, params: AssetParams = {}) {
    const { tokens, isLoading: tokensLoading, walletAddress } = useUserTokens();
    const { getMetadata } = useTokenMetadata();

    const includeLpPositions = params?.includeLpPositions ?? false;
    const shouldSkip = params?.skip || !wallet || walletAddress !== wallet;

    const tokenAccounts = shouldSkip ? undefined : tokens;

    const possibleWpNfts = tokenAccounts
        ?.filter((t) => t.tokenAmount.uiAmount === 1 && t.tokenAmount.decimals === 0)
        .map((m) => m.mint);

    const { data: wp, isLoading: wpLoading } = useWhirlpoolPositions(possibleWpNfts, {
        skip: shouldSkip || !includeLpPositions
    });

    const whirlpoolsAsTokens = wp?.map(whirlpoolPositionToToken);

    const { data: raydiumPositions, isLoading: raydiumPositionsLoading } = useRaydiumPositions(possibleWpNfts, {
        skip: shouldSkip || !includeLpPositions
    });

    const raydiumPositionsAsTokens = raydiumPositions?.map(raydiumPositionToToken);

    const clmmPositions = combineCollections([whirlpoolsAsTokens, raydiumPositionsAsTokens]);

    if (shouldSkip) return undefined;

    if (tokensLoading || (wpLoading && includeLpPositions) || (raydiumPositionsLoading && includeLpPositions))
        return undefined;

    if (tokenAccounts === undefined) return undefined;

    const totalSolReserve = (params?.solBuffer ?? 0) + MIN_SOL_BALANCE;

    return tokenAccounts
        .map(
            ({
                mint,
                tokenAmount: { uiAmount: uiAmountRaw, amount, decimals }
            }): NullableRecord<TokenBalanceExpanded> => {
                const metadata = getMetadata(mint);

                const uiAmount = uiAmountRaw !== null ? uiAmountRaw : lamportsToUiAmount(parseInt(amount), decimals);
                const uiAmountAdjusted =
                    !params?.fullSolBalance && mint === WRAPPED_SOL_MINT
                        ? Math.max(0, bsMath.tokenSub(uiAmount, totalSolReserve, decimals))
                        : uiAmount;

                return {
                    key: mint,
                    metadata,
                    amount: uiAmountAdjusted
                };
            }
        )
        .concat(clmmPositions ?? [])
        .filter(filterNullableRecord)
        .filter(({ amount }) => !!amount);
}

export function useWalletBalanceByMint(wallet: string | undefined, mint: string | undefined, params: AssetParams = {}) {
    const assets = useWalletAvailableAssets(wallet, params);
    const data = assets?.find((a) => a.metadata.mint === mint);

    return { data, isLoading: !assets };
}

export function useUserEscrowsAndWalletSummarized(options?: {
    skipWallet?: boolean;
    customWallet?: string;
    includeStakedSol?: boolean;
}): TokenBalanceExpanded[] | undefined {
    const { activeWallet, isLoading: isActiveWalletLoading } = useActiveWallet();
    const wallet = options?.customWallet ?? activeWallet;
    const walletAssets = useWalletAvailableAssets(wallet, {
        skip: options?.skipWallet || !wallet || isActiveWalletLoading
    });

    if (!walletAssets) return undefined;

    const amountMap = new Map<string, TokenBalanceExpanded>();

    for (const asset of walletAssets) {
        const prevAmount = amountMap.get(asset.metadata.mint)?.amount ?? 0;
        amountMap.set(asset.metadata.mint, { ...asset, amount: prevAmount + asset.amount });
    }

    return Array.from(amountMap.values());
}

export function useUserUnrecognizedAssets(skip?: boolean) {
    const { activeWallet } = useActiveWallet();
    const { tokens, isLoading: tokensLoading, walletAddress } = useUserTokens();
    const { getMetadata, isLoading: isMetadataLoading } = useTokenMetadata();

    const shouldSkip = skip || !activeWallet || walletAddress !== activeWallet;

    const data = shouldSkip ? undefined : tokens?.filter((t) => !getMetadata(t.mint));

    const isLoading = shouldSkip ? false : tokensLoading || isMetadataLoading;

    return { data, isLoading };
}

//Balance map is a map of allowed tokens to their amount and usd value. For CLMMs, this is the POOL to the amount of postiions.
export function useSummarizedUserAssets(params: AssetParams) {
    const { data: balances, isLoading } = useUserAvailableAssets(params);
    const { getUsdPrice } = useOraclePrices(
        balances?.map((b) => b.key),
        PriceFetchType.Twap
    );

    const balanceMap = useMemo(() => {
        if (!balances) return undefined;

        const balanceMap = balances.reduce((map, curr) => {
            const key = getAssetKey(curr);

            const prev = map.get(key) ?? { available: 0, availableUsd: 0 };
            const currentUsdValue = (getUsdPrice(key) ?? 0) * curr.amount;

            if (curr.clmmPosition) {
                map.set(key, {
                    available: prev.available + 1,
                    availableUsd: Math.max(prev.availableUsd, curr.clmmPosition.totalPrice ?? 0)
                });
            } else {
                map.set(key, {
                    available: prev.available + curr.amount,
                    availableUsd: prev.availableUsd + currentUsdValue
                });
            }
            return map;
        }, new Map<string, { available: number; availableUsd: number }>());

        return balanceMap;
    }, [balances, getUsdPrice]);

    return { balanceMap, balances, isLoading };
}

function getAssetKey(asset: TokenBalanceExpanded) {
    if (asset.clmmPosition) {
        if (isWhirlpoolPosition(asset.clmmPosition)) {
            return asset.clmmPosition.whirlpoolAddress;
        } else if (isRaydiumPosition(asset.clmmPosition)) {
            return asset.clmmPosition.poolAddress;
        }
    }
    return asset.metadata.mint;
}
